import type { AdminLoginResult, AdminMessageResponse, TokenPair } from "@nutrayan/api-client";
import { apiClient } from "./api-client";

// All admin authentication + credential (OTP) endpoints in one typed place, so the
// login UI stays presentational. Mirrors the backend AdminAuthController and
// AdminCredentialController contracts (docs/api/openapi.json).

/** Next step for an email before asking for a password (BLK-API-016). Coarse by
 * design (anti-enumeration): only PASSWORD_SETUP_REQUIRED is ever revealed; any
 * other account state (or an unknown email) returns PASSWORD_REQUIRED. */
export interface AdminLoginStatus {
  state: "PASSWORD_REQUIRED" | "PASSWORD_SETUP_REQUIRED" | string;
}

export function adminLoginStatus(email: string): Promise<AdminLoginStatus> {
  return apiClient.request<AdminLoginStatus>("/api/v1/auth/admin/login/status", {
    method: "POST",
    body: { email },
  });
}

export function adminLogin(email: string, password: string): Promise<AdminLoginResult> {
  return apiClient.request<AdminLoginResult>("/api/v1/auth/admin/login", {
    method: "POST",
    body: { email, password },
  });
}

export function adminVerifyMfa(mfaToken: string, totpCode: string): Promise<TokenPair> {
  return apiClient.request<TokenPair>("/api/v1/auth/admin/mfa/verify", {
    method: "POST",
    body: { mfaToken, totpCode },
  });
}

export function adminEnrollMfa(mfaToken: string, totpCode: string): Promise<TokenPair> {
  return apiClient.request<TokenPair>("/api/v1/auth/admin/mfa/enroll", {
    method: "POST",
    body: { mfaToken, totpCode },
  });
}

/** Optional-MFA rollout only: exchange the challenge token for tokens without enrolling. */
export function adminSkipMfa(mfaToken: string): Promise<TokenPair> {
  return apiClient.request<TokenPair>("/api/v1/auth/admin/mfa/skip", {
    method: "POST",
    body: { mfaToken },
  });
}

// --- Credential (OTP) flows: first-time set-password, forgot-password, unlock. ---
// Each *request* endpoint returns a generic acknowledgement (anti-enumeration);
// each *confirm* endpoint applies the change and does NOT auto-login.

export function adminRequestPasswordSetup(email: string): Promise<AdminMessageResponse> {
  return apiClient.request<AdminMessageResponse>("/api/v1/auth/admin/password/setup/request", {
    method: "POST",
    body: { email },
  });
}

export function adminConfirmPasswordSetup(
  email: string,
  code: string,
  newPassword: string,
): Promise<AdminMessageResponse> {
  return apiClient.request<AdminMessageResponse>("/api/v1/auth/admin/password/setup/confirm", {
    method: "POST",
    body: { email, code, newPassword },
  });
}

export function adminRequestPasswordReset(email: string): Promise<AdminMessageResponse> {
  return apiClient.request<AdminMessageResponse>("/api/v1/auth/admin/password/forgot/request", {
    method: "POST",
    body: { email },
  });
}

export function adminConfirmPasswordReset(
  email: string,
  code: string,
  newPassword: string,
): Promise<AdminMessageResponse> {
  return apiClient.request<AdminMessageResponse>("/api/v1/auth/admin/password/forgot/confirm", {
    method: "POST",
    body: { email, code, newPassword },
  });
}

export function adminRequestUnlock(email: string): Promise<AdminMessageResponse> {
  return apiClient.request<AdminMessageResponse>("/api/v1/auth/admin/password/unlock/request", {
    method: "POST",
    body: { email },
  });
}

export function adminConfirmUnlock(email: string, code: string): Promise<AdminMessageResponse> {
  return apiClient.request<AdminMessageResponse>("/api/v1/auth/admin/password/unlock/confirm", {
    method: "POST",
    body: { email, code },
  });
}
